import "@tento-ui/init";


import { DarkMode } from "@tento-ui/dark-mode";
DarkMode.toggle(false, false);

import "@tento-ui/components/pages/auth-shell";
import "@tento-ui/components/pages/auth-content";
import "@tento-ui/components/pages/auth-page-status";
import "@tento-ui/components/pages/mobile-capture-card";
import "@tento-ui/components/round";
import "@tento-ui/auth/google-signin-button";
import "@tento-ui/auth/microsoft-signin-button";
import "@tento-ui/brand/landing/gradient-splat-wash";
import "@tento-ui/dev-mode";

import { AuthApi } from "@tento-ui/auth/auth";
import { GoogleSigninButton } from "@tento-ui/auth/google-signin-button";
import { MicrosoftSigninButton } from "@tento-ui/auth/microsoft-signin-button";
import { AuthShell } from "@tento-ui/components/pages/auth-shell";
import { AuthContent } from "@tento-ui/components/pages/auth-content";
import { AuthPageStatus } from "@tento-ui/components/pages/auth-page-status";
import { MobileCaptureCard } from "@tento-ui/components/pages/mobile-capture-card";
import { $ } from "@tento-ui/dom-selectors";
import { Tracing } from "@tento-core/tracing";
import { LonaApi } from "../../lib/api";
import { LonaFeatureFlags } from "../../lib/core/flags";
import { MicrosoftAuth, OAuthPopup } from "../../lib/settings/oauth-popup";
import { InstanceUrl } from "@tento-core/instance-url";
import type { GradientSplatWash } from "@tento-ui/brand/landing/gradient-splat-wash";

import { SigninInterfaceRecorder, type InterfaceReason } from "../../lib/signin-interface";

const url = new URL(window.location.href);
const redirect = url.searchParams.get("redirect_url");
const promo = url.searchParams.get("promo") ?? url.searchParams.get("key");

// Force-fresh-auth mode (`?fresh=1`): drop any existing session up front so a
// stale signed-in session can never carry the user past Google OAuth. Used by
// the onboarding UX tests, whose runs must always start signed out (a
// server-side account reset cannot clear the operator's browser cookie). The
// signout promise is awaited before the signin POST — not before the click —
// so it can never race the new session's creation. Non-fatal: worst case the
// old session survives and signin simply re-logs-in over it.
const freshAuth: Promise<void> =
  url.searchParams.get("fresh") === "1"
    ? AuthApi.postResult("/_/auth/signout").then(
        () => undefined,
        (e) =>
          Tracing.warn("net")("fresh-auth signout failed (non-fatal)", {
            error: String(e),
          }),
      )
    : Promise.resolve();
const $signin = $<GoogleSigninButton>("google-signin");
$<GradientSplatWash>("ambient-wash").bind({ wash: "sunset-1" });

function isMobileOnboardingDevice(): boolean {
  const mobileUserAgent =
    /Android|BlackBerry|iPad|iPhone|iPod|IEMobile|Mobile|Opera Mini|webOS/i
      .test(navigator.userAgent);
  return mobileUserAgent || window.innerWidth <= 680;
}

if (promo) {
  const $status = $<AuthPageStatus>("status");
  // Claim the promo code server-side to lock it for this browser session.
  // Route types available after `./run lona-admin gen.ts`.
  LonaApi.postResult("/api/public/promo/claim", {
    query: { code: promo },
  }).then((r) => {
    r.match({
      ok: (result) => {
        if (result.status === "claimed") {
          $status.bind({ variant: "promo", message: "Promo code ready to apply", code: promo });
        } else if (result.status === "already_claimed") {
          $status.bind({ variant: "error", message: "This promo link has already been claimed by someone else", code: null });
        } else {
          $status.bind({ variant: "error", message: "Invalid promo code", code: null });
        }
        $status.style.display = "flex";
      },
      err: () => {
        // Graceful degradation — show promo status anyway
        $status.bind({ variant: "promo", message: "Promo code ready to apply", code: promo });
        $status.style.display = "flex";
      },
    });
  });
}

const $page = $<AuthShell>("root");

if ($page.isConnected) {
  $page.style.opacity = "1";
} else {
  $page.onConnected = () => {
    $page.style.opacity = "1";
  };
}

// Shared post-signin routing — both the google code flow and the
// outlook popup flow land here once the session cookie exists.
function routeAfterSignin(promoApplied: boolean): void {
  let targetUrl: string;
  if (isMobileOnboardingDevice()) {
    targetUrl = InstanceUrl.calendar("/onboarding");
  } else if (redirect) {
    targetUrl = redirect;
  } else if (promoApplied) {
    // Promo info is now carried via last_promo cookie + API, not URL params
    targetUrl = InstanceUrl.calendar("/onboarding#plan-options");
  } else {
    targetUrl = InstanceUrl.calendar();
  }

  location.href = targetUrl;
}

function showSigninError(reason: string, providerName: string): void {
  const $status = $<AuthPageStatus>("status");
  let message: string;
  switch (reason) {
    case "missing_scopes":
      message = `Lona needs access to your ${providerName} calendar and events in order to work.`;
      break;
    case "access_denied":
      message = "Sign-in was cancelled. Try again whenever you're ready.";
      break;
    default:
      message = `Something went wrong! Please try again later. ${reason}`;
  }
  $status.bind({ variant: "error", message, code: null });
  $status.style.display = "flex";
}

$signin.onReceiveCode = async (code: string) => {
  await freshAuth;
  const r = await LonaApi.postResult("/api/user/signin", {
    query: {
      code,
      promo: promo ?? null,
    },
  });

  r.match({
    ok: (result) => {
      routeAfterSignin(Boolean(result.promoStatus || promo));
    },
    err: (e) => {
      showSigninError(e.reason, "Google");
    },
  });
};

// "Continue with Microsoft" — whoop-style popup. The server callback
// (`/api/user/signin/outlook/redirect`) registers/logs-in the user and
// sets the session cookie on the popup response, then posts
// outlook-auth-{success|failure} back here. Hidden until the Entra app
// registration is configured AND the `outlook-provider` debug flag is
// on (outlook surfaces are debug-gated until Slice 1 ships). Promo
// redemption is google-only for now, so route as promo-less.
const $microsoftSignin = $<MicrosoftSigninButton>("microsoft-signin");
const microsoftConfigured =
  MicrosoftAuth.isConfigured() && LonaFeatureFlags.debug.outlookProvider.value;
// Microsoft visibility is owned by updateSigninSurface() below (so the mobile
// capture card can hide it); here we only wire the press handler.
if (microsoftConfigured) {
  $microsoftSignin.onPress = async () => {
    await freshAuth;
    const opened = OAuthPopup.open({
      authUrl: MicrosoftAuth.authorizeUrl(MicrosoftAuth.SIGNIN_REDIRECT_PATH),
      source: "outlook",
      onSuccess: () => routeAfterSignin(false),
      onFailure: (reason) => showSigninError(reason, "Outlook"),
      onDismiss: null,
    });
    if (!opened) {
      showSigninError("popup_blocked", "Outlook");
    }
  };
}

// --- Mobile email-capture surface (S1) -------------------------------------
// On mobile (`isMobileOnboardingDevice()` UA/width OR a live `max-width: 680px`
// media match), the OAuth buttons are replaced by an email-capture card that
// POSTs the existing anon waitlist endpoint (which already fires the CAPI
// Lead). "Sign in anyway" reveals the normal OAuth buttons for the rest of the
// session. The promo-claim flow above still runs in the background, unaffected.
const $mobileCapture = $<MobileCaptureCard>("mobile-capture");
const $authContent = $<AuthContent>("auth-content");
const mobileMediaQuery = window.matchMedia("(max-width: 680px)");

let revealed = false;
let captureShownLogged = false;
let captureState: MobileCaptureCard.CaptureState = "idle";
let captureEmail: Option<string> = null;
let captureErrorText: Option<string> = null;

// `?confirmation[=email]` (test hook): jump the capture card straight into
// its sent/receipt state — and force the capture surface on regardless of
// device — so the confirmation UI can be inspected without a real waitlist
// submit. No server call is made.
const confirmation = url.searchParams.get("confirmation");
if (confirmation !== null) {
  captureState = "sent";
  captureEmail = confirmation !== "" ? confirmation : "you@example.com";
}

function isMobileSurface(): boolean {
  const mobileUserAgent =
    /Android|BlackBerry|iPad|iPhone|iPod|IEMobile|Mobile|Opera Mini|webOS/i
      .test(navigator.userAgent);
  return mobileUserAgent || mobileMediaQuery.matches;
}

function bindCapture(): void {
  $mobileCapture.bind({
    state: captureState,
    email: captureEmail,
    errorText: captureErrorText,
    onSubmit: (email) => void submitCapture(email),
    onSignInAnyway: () => {
      revealed = true;
      Tracing.log("signin-mobile-capture", { event: "reveal" });
      updateSigninSurface("sign_in_anyway");
    },
  });
}

async function submitCapture(email: string): Promise<void> {
  captureState = "sending";
  captureEmail = email;
  captureErrorText = null;
  bindCapture();
  Tracing.log("signin-mobile-capture", { event: "submit" });

  // `source` gates the server's desktop-link email send (S2): only
  // mobile-signin submissions get the email; the classic waitlist page
  // stays record-only.
  const res = await LonaApi.postResult("/api/user/waitlist", {
    body: { email, source: "mobile-signin" },
  });

  res.match({
    ok: () => {
      captureState = "sent";
      bindCapture();
      Tracing.log("signin-mobile-capture", { event: "sent" });
    },
    err: (e) => {
      captureState = "error";
      captureErrorText =
        e.code === 422 ? "That doesn't look like a valid email." : null;
      bindCapture();
      Tracing.log("signin-mobile-capture", { event: "error", code: e.code });
    },
  });
}

const interfaceRecorder = new SigninInterfaceRecorder();

function updateSigninSurface(reason: InterfaceReason = "viewport_change"): void {
  // The mobile capture card and the auth content are sibling children of the
  // shell (both fill the round-bg panel). On mobile we swap the WHOLE contents:
  // hide the auth content and show the capture card. "Sign in anyway" flips
  // `revealed` and brings the auth content back for the rest of the session.
  const showCapture = (isMobileSurface() || confirmation !== null) && !revealed;
  if (showCapture) {
    bindCapture();
    $authContent.style.display = "none";
    $mobileCapture.style.display = "";
    if (!captureShownLogged) {
      captureShownLogged = true;
      Tracing.log("signin-mobile-capture", { event: "show" });
    }
  } else {
    $mobileCapture.style.display = "none";
    $authContent.style.display = "";
    // Google is always visible in the auth content; Microsoft only when its
    // Entra app registration is configured. (Only meaningful here, where the
    // auth content is shown.)
    $signin.style.display = "";
    $microsoftSignin.style.display = microsoftConfigured ? "flex" : "none";
  }
  // Confirmation previews are not acquisition exposures. Wait for layout and
  // foreground visibility before reporting the interface, including re-entry
  // from a background tab. Repeated resize callbacks remain deduplicated.
  if (confirmation === null) requestAnimationFrame(() => {
    const element = showCapture ? $mobileCapture : $authContent;
    if (document.visibilityState === "visible" && element.isConnected && element.getBoundingClientRect().height > 0) {
      interfaceRecorder.record(showCapture ? "email_capture" : "oauth", reason);
    }
  });
}

document.addEventListener("visibilitychange", () => updateSigninSurface());

// Live-swap on resize (e.g. rotating a tablet across the 680px breakpoint).
mobileMediaQuery.addEventListener("change", () => updateSigninSurface());
updateSigninSurface();
